import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Image as ImageIcon,
  FileText,
  TrendingUp,
  Edit,
  Upload,
} from 'lucide-react';
import * as planService from '../../../services/planService';

/**
 * Exercise Detail Component
 * Detailed view of an exercise with video, images, instructions, and progressive overload history
 */
export default function ExerciseDetail({ exerciseId, onBack }) {
  const [exercise, setExercise] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (exerciseId) {
      loadExercise();
    }
  }, [exerciseId]);

  const loadExercise = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getExerciseDetails(exerciseId);
      if (result.success) {
        setExercise(result.data);
        setNotes(result.data.client_notes || '');
      }
    } catch (error) {
      console.error('Error loading exercise:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-600">Exercise not found</p>
        <button
          onClick={onBack}
          className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
        >
          ← Back to Library
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Library</span>
        </button>
      </div>

      {/* Exercise Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{exercise.exercise_name}</h1>
        <div className="flex flex-wrap gap-2 mb-4">
          {exercise.muscle_groups?.map((group, idx) => (
            <span
              key={idx}
              className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full font-medium"
            >
              {group.charAt(0).toUpperCase() + group.slice(1).replace('_', ' ')}
            </span>
          ))}
          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full font-medium">
            {exercise.equipment_type?.charAt(0).toUpperCase() +
              exercise.equipment_type?.slice(1).replace('_', ' ')}
          </span>
        </div>
        {exercise.description && (
          <p className="text-gray-700 mb-4">{exercise.description}</p>
        )}
      </div>

      {/* Media Section */}
      {(exercise.video_url || (exercise.image_urls && exercise.image_urls.length > 0)) && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Media</h2>
          
          {/* Video */}
          {exercise.video_url && (
            <div className="mb-6">
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                <iframe
                  src={exercise.video_url}
                  className="w-full h-full"
                  allowFullScreen
                  title={exercise.exercise_name}
                />
              </div>
            </div>
          )}

          {/* Image Gallery */}
          {exercise.image_urls && exercise.image_urls.length > 0 && (
            <div>
              <div className="mb-4">
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={exercise.image_urls[activeImageIndex]}
                    alt={`${exercise.exercise_name} - Image ${activeImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              {exercise.image_urls.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {exercise.image_urls.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                        activeImageIndex === idx
                          ? 'border-primary-600'
                          : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={url}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      {exercise.instructions && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Instructions
          </h2>
          <div className="prose max-w-none">
            <p className="text-gray-700 whitespace-pre-line">{exercise.instructions}</p>
          </div>
        </div>
      )}

      {/* Form Cues */}
      {exercise.form_cues && exercise.form_cues.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Form Cues</h2>
          <ul className="space-y-2">
            {exercise.form_cues.map((cue, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span className="text-gray-700">{cue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Default Sets/Reps */}
      {(exercise.default_sets || exercise.default_reps || exercise.default_weight) && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recommended</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {exercise.default_sets && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Sets</p>
                <p className="text-2xl font-bold text-gray-900">{exercise.default_sets}</p>
              </div>
            )}
            {exercise.default_reps && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Reps</p>
                <p className="text-2xl font-bold text-gray-900">{exercise.default_reps}</p>
              </div>
            )}
            {exercise.default_weight && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Weight</p>
                <p className="text-2xl font-bold text-gray-900">{exercise.default_weight} lbs</p>
              </div>
            )}
            {exercise.default_rest_seconds && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Rest</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.floor(exercise.default_rest_seconds / 60)}:
                  {(exercise.default_rest_seconds % 60).toString().padStart(2, '0')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Client Notes */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Edit className="w-5 h-5" />
            My Notes
          </h2>
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            {showNotes ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {showNotes ? (
          <div className="space-y-3">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Add your personal notes about this exercise..."
            />
            <button
              onClick={() => {
                // TODO: Save notes to exercise
                setShowNotes(false);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Save Notes
            </button>
          </div>
        ) : (
          <div>
            {notes ? (
              <p className="text-gray-700 whitespace-pre-line">{notes}</p>
            ) : (
              <p className="text-gray-400 italic">No notes yet. Click Edit to add your personal notes.</p>
            )}
          </div>
        )}
      </div>

      {/* Form Analysis Upload */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Form Analysis
        </h2>
        <p className="text-gray-600 mb-4">
          Upload a video of yourself performing this exercise for coach review.
        </p>
        <button className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-600 hover:text-primary-600 transition-colors w-full">
          Upload Video
        </button>
      </div>
    </div>
  );
}
